import { LocationKeys } from "./location";
import { TursoConfig, resolveOrganization } from "./config";
import { TursoClient } from "./client";

export interface Database {
  name: string;
  id: string;
  hostname: string;
  regions?: Array<keyof LocationKeys>;
  primaryRegion?: keyof LocationKeys;
  type: string;
  version: string;
  group?: string;
  sleeping: boolean;
  archived: boolean;
  allow_attach: boolean;
  block_reads: boolean;
  block_writes: boolean;
  schema?: string;
  is_schema: boolean;
}

export interface ApiDatabaseResponse
  extends Database,
  ApiCreateDatabaseResponse { }

export interface ApiCreateDatabaseResponse {
  DbId: string;
  Hostname: string;
  Name: string;
}

export interface CreatedDatabase {
  name: string;
  id: string;
  hostname: string;
}

interface DatabaseInstanceUsageDetail {
  rows_read: number;
  rows_written: number;
  storage_bytes: number;
}

interface DatabaseInstanceUsage {
  uuid: string;
  usage: DatabaseInstanceUsageDetail;
}

export interface DatabaseUsage {
  uuid: string;
  instances: DatabaseInstanceUsage[];
  usage: DatabaseInstanceUsageDetail;
}

export interface InstanceUsages {
  [instanceUuid: string]: DatabaseInstanceUsageDetail;
}

export interface TotalUsage {
  rows_read: number;
  rows_written: number;
  storage_bytes: number;
}

export interface DatabaseInstance {
  uuid: string;
  name: keyof LocationKeys;
  type: "primary" | "replica";
  region: keyof LocationKeys;
  hostname: string;
}

export interface DeletedDatabase {
  database: string;
}

export interface DatabaseToken {
  jwt: string;
}

export type EncryptionCipher =
  | "aes256gcm"
  | "aes128gcm"
  | "chacha20poly1305"
  | "aegis128l"
  | "aegis128x2"
  | "aegis128x4"
  | "aegis256"
  | "aegis256x2"
  | "aegis256x4";

export interface RemoteEncryption {
  /**
   * Base64-encoded encryption key. Key size depends on the cipher: 32 bytes
   * for aes256gcm, chacha20poly1305 and aegis256 variants; 16 bytes for
   * aes128gcm and aegis128l variants.
   */
  encryption_key: string;
  encryption_cipher: EncryptionCipher;
}

type MultiDBSchemaOptions =
  | { is_schema: boolean; schema?: never }
  | { is_schema?: never; schema: string }
  | {};

function hasIsSchemaOption(
  options: any
): options is { is_schema: boolean; schema?: never } {
  return options !== undefined && options.is_schema !== undefined;
}

function hasSchemaOption(
  options: any
): options is { is_schema?: never; schema: string } {
  return options !== undefined && options.schema !== undefined;
}

/**
 * Methods that operate on a single database take a `dbName` positional
 * argument that identifies the database in the request path. On v1/v2 of the
 * Turso API this is the database name; on v3 it is the database id.
 */
export class DatabaseClient {
  constructor(private config: TursoConfig) { }

  private get org(): string {
    return resolveOrganization(this.config);
  }

  async list(options?: {
    schema?: string;
    group?: string;
    type?: "schema";
  }): Promise<Database[]> {
    const queryParams = new URLSearchParams(
      Object.entries({
        schema: options?.schema,
        group: options?.group,
        type: options?.type,
      }).filter(([_, value]) => value !== undefined) as [string, string][]
    );

    const url = `organizations/${this.org}/databases${queryParams.toString() ? `?${queryParams.toString()}` : ""}`;

    const response = await TursoClient.request<{
      databases: ApiDatabaseResponse[];
    }>(url, this.config);

    return (response.databases ?? []).map((db) => this.formatResponse(db));
  }

  async get(dbName: string): Promise<Database> {
    const response = await TursoClient.request<{
      database: ApiDatabaseResponse;
    }>(`organizations/${this.org}/databases/${dbName}`, this.config);

    return this.formatResponse(response.database);
  }

  async create(
    dbName: string,
    options?: {
      image?: "latest" | "canary";
      /** Name of the group where the database should be created. */
      group?: string;
      /**
       * Id of the group where the database should be created. Mutually
       * exclusive with `group`. Requires v3 of the Turso API.
       */
      groupId?: string;
      seed?: {
        type: "database" | "dump";
        name?: string;
        url?: string;
        timestamp?: string | Date;
      };
      size_limit?: string;
      remote_encryption?: RemoteEncryption;
      /** When true, provisions the database using TursoDB. */
      useTursoDb?: boolean;
    } & MultiDBSchemaOptions
  ): Promise<CreatedDatabase> {
    if (hasIsSchemaOption(options) && hasSchemaOption(options)) {
      throw new Error("'is_schema' and 'schema' cannot both be provided");
    }

    if (options?.group !== undefined && options?.groupId !== undefined) {
      throw new Error("'group' and 'groupId' cannot both be provided");
    }

    if (options?.seed) {
      if (options.seed.type === "database" && !options.seed.name) {
        throw new Error("Seed name is required when type is 'database'");
      }
      if (options.seed.type === "dump" && !options.seed.url) {
        throw new Error("Seed URL is required when type is 'dump'");
      }
    }

    if (options?.seed?.timestamp) {
      options.seed.timestamp = this.formatDateParameter(options.seed.timestamp);
    }

    const { groupId, useTursoDb, ...rest } = options ?? {};

    const response = await TursoClient.request<{
      database: ApiCreateDatabaseResponse;
    }>(`organizations/${this.org}/databases`, this.config, {
      method: "POST",
      headers: {
        "content-type": "application/json",
      },
      body: JSON.stringify({
        name: dbName,
        ...rest,
        ...(groupId !== undefined ? { group_id: groupId } : {}),
        ...(useTursoDb !== undefined ? { use_tursodb: useTursoDb } : {}),
      }),
    });

    return this.formatCreateResponse(response.database);
  }

  async updateVersion(dbName: string): Promise<void> {
    return await TursoClient.request(
      `organizations/${this.org}/databases/${dbName}/update`,
      this.config,
      {
        method: "POST",
      }
    );
  }

  async delete(dbName: string) {
    const response = await TursoClient.request<DeletedDatabase>(
      `organizations/${this.org}/databases/${dbName}`,
      this.config,
      {
        method: "DELETE",
      }
    );

    return response;
  }

  async listInstances(dbName: string): Promise<DatabaseInstance[]> {
    const response = await TursoClient.request<{
      instances: DatabaseInstance[];
    }>(
      `organizations/${this.org}/databases/${dbName}/instances`,
      this.config
    );

    return response.instances ?? [];
  }

  async getInstance(
    dbName: string,
    instanceName: keyof LocationKeys
  ): Promise<DatabaseInstance> {
    const response = await TursoClient.request<{
      instance: DatabaseInstance;
    }>(
      `organizations/${this.org}/databases/${dbName}/instances/${instanceName}`,
      this.config
    );

    return response.instance ?? null;
  }

  async createToken(
    dbName: string,
    options?: {
      expiration?: string;
      authorization?: "read-only" | "full-access";
      permissions?: {
        read_attach: { databases: Database["name"][] };
      };
    }
  ): Promise<DatabaseToken> {
    const queryParams = new URLSearchParams();

    if (options?.expiration) {
      queryParams.set("expiration", options.expiration);
    }

    if (options?.authorization) {
      queryParams.set("authorization", options.authorization);
    }

    const response = await TursoClient.request<DatabaseToken>(
      `organizations/${this.org}/databases/${dbName}/auth/tokens?${queryParams}`,
      this.config,
      {
        method: "POST",
        body: JSON.stringify({
          permissions: {
            read_attach: {
              databases: options?.permissions?.read_attach?.databases ?? [],
            },
          },
        }),
      }
    );

    return response;
  }

  async rotateTokens(dbName: string): Promise<void> {
    return await TursoClient.request<void>(
      `organizations/${this.org}/databases/${dbName}/auth/rotate`,
      this.config,
      {
        method: "POST",
      }
    );
  }

  async usage(
    dbName: string,
    options?: { from?: Date | string; to?: Date | string }
  ): Promise<DatabaseUsage> {
    const queryParams = new URLSearchParams();

    if (options?.from) {
      queryParams.set("from", this.formatDateParameter(options.from));
    }

    if (options?.to) {
      queryParams.set("to", this.formatDateParameter(options.to));
    }

    const response = await TursoClient.request<{
      database: DatabaseUsage;
      instances: InstanceUsages;
      total: TotalUsage;
    }>(
      `organizations/${this.org}/databases/${dbName}/usage?${queryParams}`,
      this.config
    );

    return response.database;
  }

  private formatDateParameter(date: Date | string): string {
    return date instanceof Date ? date.toISOString() : date;
  }

  private formatResponse(db: ApiDatabaseResponse): Database {
    return {
      name: db.Name,
      id: db.DbId,
      hostname: db.Hostname,
      regions: db.regions,
      primaryRegion: db.primaryRegion,
      type: db.type,
      version: db.version,
      group: db.group,
      sleeping: db.sleeping,
      archived: db.archived,
      allow_attach: db.allow_attach,
      block_reads: db.block_reads,
      block_writes: db.block_writes,
      schema: db.schema,
      is_schema: db.is_schema,
    };
  }

  private formatCreateResponse(db: ApiCreateDatabaseResponse): CreatedDatabase {
    return {
      id: db.DbId,
      hostname: db.Hostname,
      name: db.Name,
    };
  }
}
